import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { DndContext, PointerSensor, KeyboardSensor, useSensor, useSensors, closestCenter } from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  arrayMove,
  verticalListSortingStrategy,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { KeenIcon } from '@/components';
import recordTypeService from '@/services/recordType/recordTypeService';
import { invalidateRecordTypeLayout } from '@/hooks/useRecordTypeLayout';

const errorText = (error, fallback) =>
  (error?.errors && Object.values(error.errors).flat()[0]) || error?.message || fallback;

// ---- One stage row: drag handle, colour, name, usage, retire/restore, delete ----
const StageRow = ({ stage, position, busy, onRename, onRecolor, onToggleActive, onDelete }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: stage.id });
  const [name, setName] = useState(stage.name);
  const inUse = (stage.matters_count ?? 0) > 0;

  useEffect(() => setName(stage.name), [stage.name]);

  const commitName = () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setName(stage.name);
      return;
    }
    if (trimmed !== stage.name) {
      // A refused rename (a name the type already has) puts the saved name back.
      Promise.resolve(onRename(stage, trimmed)).then((ok) => {
        if (!ok) setName(stage.name);
      });
    }
  };

  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.6 : 1 }}
      className={`flex items-center gap-2 p-2 border rounded-md border-border bg-white dark:bg-coal-500 ${
        stage.is_active ? '' : 'opacity-70'
      }`}
    >
      <button
        type="button"
        className="text-gray-400 cursor-grab hover:text-gray-600"
        aria-label={`Drag to reorder ${stage.name}`}
        {...attributes}
        {...listeners}
      >
        <KeenIcon icon="dots-square" />
      </button>
      <span className="w-5 text-xs text-right text-gray-500">{position}</span>
      <label className="relative shrink-0" title="Change colour">
        <span
          className="block border rounded-full size-5 border-border"
          style={{ backgroundColor: stage.color || '#6B7280' }}
        />
        <input
          type="color"
          className="absolute inset-0 opacity-0 cursor-pointer"
          value={stage.color || '#6B7280'}
          onChange={(e) => onRecolor(stage, e.target.value)}
          aria-label={`Colour for ${stage.name}`}
        />
      </label>
      <input
        className="flex-1 min-w-0 input input-sm"
        value={name}
        maxLength={100}
        disabled={busy}
        onChange={(e) => setName(e.target.value)}
        onBlur={commitName}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            e.currentTarget.blur();
          }
          if (e.key === 'Escape') setName(stage.name);
        }}
        aria-label="Stage name"
      />
      {!stage.is_active ? <span className="badge badge-sm badge-outline badge-secondary">Retired</span> : null}
      <span className="w-20 text-xs text-right text-gray-500 whitespace-nowrap">
        {stage.matters_count ?? 0} matter{stage.matters_count === 1 ? '' : 's'}
      </span>
      <button
        type="button"
        className="btn btn-xs btn-light"
        disabled={busy}
        onClick={() => onToggleActive(stage)}
        title={stage.is_active ? 'Matters keep it, but it can no longer be picked' : 'Make it pickable again'}
      >
        {stage.is_active ? 'Retire' : 'Restore'}
      </button>
      <button
        type="button"
        className="btn btn-xs btn-icon btn-light"
        disabled={busy || inUse}
        onClick={() => onDelete(stage)}
        title={inUse ? 'Matters use this stage. Retire it instead.' : 'Delete stage'}
        aria-label={`Delete ${stage.name}`}
      >
        <KeenIcon icon="trash" className={inUse ? 'text-gray-400' : 'text-red-600'} />
      </button>
    </li>
  );
};

/**
 * The firm admin's stage list for one record type (OL-208). Every change is
 * saved straight away; there is no separate Save step.
 */
const RecordTypeStagesModal = ({ recordType, onClose, onChanged }) => {
  const [stages, setStages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [newName, setNewName] = useState('');
  const colorTimers = useRef({});

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const fetchStages = async () => {
    try {
      const { data, status } = await recordTypeService.getStages(recordType.id);
      if (status) setStages(data || []);
    } catch (error) {
      toast.error(errorText(error, 'Failed to load stages.'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recordType.id]);

  // Runs one change, then reloads the list so counts and order stay true.
  const run = async (action, failure) => {
    setBusy(true);
    try {
      await action();
      invalidateRecordTypeLayout(recordType.id);
      onChanged?.();
      return true;
    } catch (error) {
      toast.error(errorText(error, failure));
      return false;
    } finally {
      await fetchStages();
      setBusy(false);
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    const name = newName.trim();
    if (!name) return;
    const ok = await run(() => recordTypeService.createStage(recordType.id, { name }), 'Could not add the stage.');
    if (ok) setNewName('');
  };

  const handleRename = (stage, name) =>
    run(() => recordTypeService.updateStage(recordType.id, stage.id, { name }), 'Could not rename the stage.');

  const handleRecolor = (stage, color) => {
    setStages((prev) => prev.map((s) => (s.id === stage.id ? { ...s, color } : s)));
    // The native picker fires on every drag of the hue slider; save once it settles.
    clearTimeout(colorTimers.current[stage.id]);
    colorTimers.current[stage.id] = setTimeout(
      () => run(() => recordTypeService.updateStage(recordType.id, stage.id, { color: color.toUpperCase() }), 'Could not save the colour.'),
      400
    );
  };

  const handleToggleActive = (stage) =>
    run(
      () => recordTypeService.updateStage(recordType.id, stage.id, { is_active: !stage.is_active }),
      'Could not update the stage.'
    );

  const handleDelete = (stage) =>
    run(() => recordTypeService.deleteStage(recordType.id, stage.id), 'Could not delete the stage.');

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const from = stages.findIndex((s) => s.id === active.id);
    const to = stages.findIndex((s) => s.id === over.id);
    const reordered = arrayMove(stages, from, to);
    setStages(reordered);
    run(() => recordTypeService.reorderStages(recordType.id, reordered.map((s) => s.id)), 'Could not save the new order.');
  };

  return (
    <div className="modal-wrapper modal-animate-fade">
      <div className="w-[95%] max-w-2xl modal-body animate-scale">
        <h3 className="card-title">Stages: {recordType.name}</h3>
        <p className="mt-1 text-sm text-gray-600">
          Matters of this type move through these stages in order. Drag to reorder. A stage that matters
          use can be retired, but not deleted.
        </p>
        <button type="button" onClick={onClose} className="modal-close-btn" aria-label="Close">
          <KeenIcon icon="cross" />
        </button>

        <div className="mt-4">
          {loading ? (
            <div className="py-8 text-sm text-center text-gray-500">Loading…</div>
          ) : (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={stages.map((s) => s.id)} strategy={verticalListSortingStrategy}>
                <ul className="flex flex-col gap-2 max-h-[55vh] overflow-y-auto pr-1">
                  {stages.map((stage, index) => (
                    <StageRow
                      key={stage.id}
                      stage={stage}
                      position={index + 1}
                      busy={busy}
                      onRename={handleRename}
                      onRecolor={handleRecolor}
                      onToggleActive={handleToggleActive}
                      onDelete={handleDelete}
                    />
                  ))}
                  {stages.length === 0 ? (
                    <li className="py-6 text-sm text-center text-gray-500">No stages yet. Add the first one below.</li>
                  ) : null}
                </ul>
              </SortableContext>
            </DndContext>
          )}

          <form onSubmit={handleAdd} className="flex items-center gap-2 mt-4">
            <input
              className="flex-1 input input-sm"
              placeholder="New stage name, e.g. Mediation"
              value={newName}
              maxLength={100}
              onChange={(e) => setNewName(e.target.value)}
              aria-label="New stage name"
            />
            <button type="submit" className="btn btn-sm btn-primary" disabled={busy || !newName.trim()}>
              <KeenIcon icon="plus" /> Add Stage
            </button>
          </form>
        </div>

        <div className="flex justify-end mt-5">
          <button type="button" className="btn btn-sm btn-light" onClick={onClose}>
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

export { RecordTypeStagesModal };
